import { colors } from '@mui/material';
import { useRecordContext, useTranslate } from 'react-admin';
import { match } from 'ts-pattern';

import {
  SavingsFundsRequestDetails,
  SavingsFundsRequestReconStatus,
  SavingsFundsRequestsDisplayListItem,
} from '@Common/types/bo/b2b/savings_funds_requests';
import { CoCardTopUpsStatus } from '@Common/types/funding/deposit';

import { SelectChipField } from '@sh4res/bo-common/fields';
import { makeChoicesCommon } from '@sh4res/bo-common/utils';

export const savingsFundsRequestStatusChoices = makeChoicesCommon<
  Exclude<SavingsFundsRequestDetails['status'], null>
>()({
  check_employer_balance:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.check_employer_balance',
  promote_orders: 'resources.bo_common.b2b.savings_funds_requests.choices.status.promote_orders',
  funds_request_paid:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.funds_request_paid',
  funds_request_completed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.funds_request_completed',
  check_employer_balance_failed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.check_employer_balance_failed',
  promote_orders_failed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.promote_orders_failed',
  funds_request_paid_failed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.funds_request_paid_failed',
  funds_request_completed_failed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.funds_request_completed_failed',
  finished: 'resources.bo_common.b2b.savings_funds_requests.choices.status.finished',
  finished_failed: 'resources.bo_common.b2b.savings_funds_requests.choices.status.finished_failed',
  cancelled: 'resources.bo_common.b2b.savings_funds_requests.choices.status.cancelled',
  cancelled_failed:
    'resources.bo_common.b2b.savings_funds_requests.choices.status.cancelled_failed',
  funds_request_pending: 'resources.bo_common.b2b.savings_funds_requests.choices.status.pending',
});

export const coPaymentStatusChoices = makeChoicesCommon<CoCardTopUpsStatus>()({
  initiated: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.initiated',
  pending: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.pending',
  captured: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.captured',
  rejected: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.rejected',
  settled: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.settled',
  authorization_declined:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.authorization_declined',
  shares_cancelled:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.shares_cancelled',
  pending_declined:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.pending_declined',
  card_verified:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.card_verified',
  card_verification_declined:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.card_verification_declined',
  returned: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.returned',
  refunded: 'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.refunded',
  partially_refunded:
    'resources.bo_common.b2b.savings_funds_requests.choices.co_payment_status.partially_refunded',
});

export const savingsFundsRequestReconStatusChoices =
  makeChoicesCommon<SavingsFundsRequestReconStatus>()({
    balanced: 'resources.bo_common.b2b.savings_funds_requests.choices.recon_status.balanced',
    over_paid: 'resources.bo_common.b2b.savings_funds_requests.choices.recon_status.over_paid',
    under_paid: 'resources.bo_common.b2b.savings_funds_requests.choices.recon_status.under_paid',
  });

export const B2bCompanyTreasurySfrColorValues = ['green', 'orange', 'red'] as const;
export type B2bCompanyTreasurySfrColor = (typeof B2bCompanyTreasurySfrColorValues)[number];

export function getB2bCompanyTreasurySfrColor({
  total_net_amount: totalNetAmount,
  processed_sum: processedSum,
  pending_sum: pendingSum,
  has_failed_final_payment: hasFailedFinalPayment,
}: {
  total_net_amount: number;
  processed_sum: number;
  pending_sum: number;
  has_failed_final_payment: boolean;
}): B2bCompanyTreasurySfrColor {
  const TOLERANCE_CENTS = 100;

  if (
    !hasFailedFinalPayment &&
    pendingSum === 0 &&
    Math.abs(totalNetAmount - processedSum) <= TOLERANCE_CENTS
  ) {
    return 'green';
  }

  if (Math.abs(totalNetAmount - (processedSum + pendingSum)) <= TOLERANCE_CENTS) {
    return 'orange';
  }

  return 'red';
}

export const treasuryColorChoices = makeChoicesCommon<B2bCompanyTreasurySfrColor>()({
  green: 'resources.bo_common.b2b.savings_funds_requests.choices.treasury_color.green',
  orange: 'resources.bo_common.b2b.savings_funds_requests.choices.treasury_color.orange',
  red: 'resources.bo_common.b2b.savings_funds_requests.choices.treasury_color.red',
});

export const savingsFundsRequestTypeChoices = makeChoicesCommon<
  SavingsFundsRequestDetails['type']
>()({
  matching_contribution:
    'resources.bo_common.b2b.savings_funds_requests.choices.type.matching_contribution',
  b2b_management_fee:
    'resources.bo_common.b2b.savings_funds_requests.choices.type.b2b_management_fee',
  b2b_custody_fee: 'resources.bo_common.b2b.savings_funds_requests.choices.type.b2b_custody_fee',
  campaign: 'resources.bo_common.b2b.savings_funds_requests.choices.type.campaign',
  ccb_withdrawal: 'resources.bo_common.b2b.savings_funds_requests.choices.type.ccb_withdrawal',
  capital_increase: 'resources.bo_common.b2b.savings_funds_requests.choices.type.capital_increase',
});

export const SavingsFundsRequestStatus = () => {
  const record = useRecordContext<SavingsFundsRequestsDisplayListItem>();
  const translate = useTranslate();
  if (!record) {
    return;
  }

  return (
    <SelectChipField
      source="status"
      data-testid="BoExternalSavingsFundRequestChoices_StatusField"
      sx={{
        backgroundColor: match(record.status)
          .with('finished', () => colors.green[500])
          .with('cancelled', () => colors.grey[500])
          .otherwise(() => colors.deepOrange[500]),
        color: 'white',
      }}
      choices={savingsFundsRequestStatusChoices}
      emptyText={translate('resources.bo_common.b2b.savings_funds_requests.choices.status.pending')}
    />
  );
};

export const SavingsFundsRequestReconStatusDisplay = () => {
  const record = useRecordContext<SavingsFundsRequestsDisplayListItem>();
  if (!record) {
    return;
  }

  return (
    <SelectChipField
      source="recon_status"
      data-testid="BoExternalSavingsFundRequestChoices_ReconStatusField"
      sx={{
        backgroundColor: match(record.recon_status)
          .with('balanced', () => colors.green[500])
          .with('over_paid', () => colors.orange[500])
          .with('under_paid', () => colors.red[500])
          .exhaustive(),
        color: 'white',
      }}
      choices={savingsFundsRequestReconStatusChoices}
    />
  );
};
export const B2bCompanyTreasuryColorDisplay = ({
  totalNetAmount,
  processedSum,
  pendingSum,
  hasFailedFinalPayment,
}: {
  totalNetAmount: number;
  processedSum: number;
  pendingSum: number;
  hasFailedFinalPayment: boolean;
}) => {
  const color = getB2bCompanyTreasurySfrColor({
    total_net_amount: totalNetAmount,
    processed_sum: processedSum,
    pending_sum: pendingSum,
    has_failed_final_payment: hasFailedFinalPayment,
  });

  return (
    <SelectChipField
      source="treasury_color"
      record={{ treasury_color: color }}
      data-testid="BoExternalB2bCompanyTreasuryColorDisplay"
      sx={{
        backgroundColor: match(color)
          .with('green', () => colors.green[500])
          .with('orange', () => colors.orange[500])
          .with('red', () => colors.red[500])
          .exhaustive(),
        color: 'white',
      }}
      choices={treasuryColorChoices}
    />
  );
};
